/**
 * Edit Tools (Transform Operations)
 * 
 * Provides functional Move, Copy, Rotate, Scale, Mirror operations
 * for selected entities. Implements 2D-style transform behavior.
 */

import { create } from 'zustand';
import * as THREE from 'three';
import { Entity } from '../../state/store';

// ============================================================================
// TYPES
// ============================================================================

export type EditMode =
    | 'none'
    | 'move'
    | 'copy'
    | 'rotate'
    | 'scale'
    | 'mirror'
    | 'array'
    | 'offset';

export interface TransformState {
    mode: EditMode;
    isActive: boolean;
    basePoint: THREE.Vector3 | null;      // First click point
    currentPoint: THREE.Vector3 | null;   // Current mouse position
    referenceAngle: number;               // For rotation
    scaleFactor: number;                  // For scale
    mirrorAxis: 'x' | 'y' | 'z' | null;   // For mirror
    copyMode: boolean;                    // Keep original when transforming

    // Actions
    setMode: (mode: EditMode) => void;
    startTransform: (basePoint: THREE.Vector3) => void;
    updateTransform: (currentPoint: THREE.Vector3) => void;
    completeTransform: () => void;
    cancelTransform: () => void;
    setMirrorAxis: (axis: 'x' | 'y' | 'z') => void;
    setCopyMode: (copy: boolean) => void;
}

// ============================================================================
// TRANSFORM STORE
// ============================================================================

export const useTransformStore = create<TransformState>((set) => ({
    mode: 'none',
    isActive: false,
    basePoint: null,
    currentPoint: null,
    referenceAngle: 0,
    scaleFactor: 1,
    mirrorAxis: null,
    copyMode: false,

    setMode: (mode) => set({
        mode,
        isActive: false,
        basePoint: null,
        currentPoint: null,
        copyMode: mode === 'copy',
    }),

    startTransform: (basePoint) => set({
        isActive: true,
        basePoint: basePoint.clone(),
        currentPoint: basePoint.clone(),
    }),

    updateTransform: (currentPoint) => set({
        currentPoint: currentPoint.clone(),
    }),

    completeTransform: () => set({
        isActive: false,
        basePoint: null,
        currentPoint: null,
    }),

    cancelTransform: () => set({
        mode: 'none',
        isActive: false,
        basePoint: null,
        currentPoint: null,
        copyMode: false,
    }),

    setMirrorAxis: (axis) => set({ mirrorAxis: axis }),
    setCopyMode: (copy) => set({ copyMode: copy }),
}));

// ============================================================================
// TRANSFORM CALCULATIONS
// ============================================================================

/**
 * Calculate move transformation
 */
export function calculateMove(
    entity: Entity,
    basePoint: THREE.Vector3,
    targetPoint: THREE.Vector3
): Partial<Entity['data']> {
    const delta = new THREE.Vector3().subVectors(targetPoint, basePoint);
    const data = entity.data;

    // Handle different entity types
    switch (entity.type) {
        case 'line':
        case 'wall': {
            const start = arrayToVector(data.start as number[]);
            const end = arrayToVector(data.end as number[]);
            return {
                start: vectorToArray(start.add(delta)),
                end: vectorToArray(end.add(delta.clone())),
                // Recalculate center for walls
                ...(data.center ? {
                    center: vectorToArray(arrayToVector(data.center as number[]).add(delta.clone())),
                } : {}),
            };
        }

        case 'circle':
        case 'arc': {
            const center = arrayToVector(data.center as number[]);
            return {
                center: vectorToArray(center.add(delta)),
            };
        }

        case 'rectangle': {
            const corner1 = arrayToVector(data.corner1 as number[]);
            const corner2 = arrayToVector(data.corner2 as number[]);
            return {
                corner1: vectorToArray(corner1.add(delta)),
                corner2: vectorToArray(corner2.add(delta.clone())),
            };
        }

        case 'box':
        case 'sphere':
        case 'cylinder': {
            const position = arrayToVector(data.position as number[] || [0, 0, 0]);
            return {
                position: vectorToArray(position.add(delta)),
            };
        }

        case 'curve': {
            const points = (data.points as number[][]).map((p) => {
                const vec = arrayToVector(p);
                return vectorToArray(vec.add(delta.clone()));
            });
            return { points };
        }

        default:
            // Generic position-based entities
            if (data.position) {
                const position = arrayToVector(data.position as number[]);
                return { position: vectorToArray(position.add(delta)) };
            }
            return {};
    }
}

/**
 * Calculate rotation transformation
 */
export function calculateRotate(
    entity: Entity,
    center: THREE.Vector3,
    angle: number
): Partial<Entity['data']> {
    const data = entity.data;
    const rotationMatrix = new THREE.Matrix4().makeRotationY(angle);

    const rotatePoint = (point: THREE.Vector3): THREE.Vector3 => {
        const local = point.clone().sub(center);
        local.applyMatrix4(rotationMatrix);
        return local.add(center);
    };

    switch (entity.type) {
        case 'line':
        case 'wall': {
            const start = rotatePoint(arrayToVector(data.start as number[]));
            const end = rotatePoint(arrayToVector(data.end as number[]));

            // For walls, recalculate angle
            const wallAngle = Math.atan2(end.z - start.z, end.x - start.x);

            return {
                start: vectorToArray(start),
                end: vectorToArray(end),
                ...(data.angle !== undefined && { angle: wallAngle }),
                ...(data.center ? {
                    center: vectorToArray(rotatePoint(arrayToVector(data.center as number[]))),
                } : {}),
            };
        }

        case 'circle': {
            const circleCenter = rotatePoint(arrayToVector(data.center as number[]));
            return { center: vectorToArray(circleCenter) };
        }

        case 'arc': {
            const arcCenter = rotatePoint(arrayToVector(data.center as number[]));
            const startAngle = (data.startAngle as number) + angle;
            const endAngle = (data.endAngle as number) + angle;
            return {
                center: vectorToArray(arcCenter),
                startAngle,
                endAngle,
            };
        }

        case 'rectangle': {
            const corner1 = rotatePoint(arrayToVector(data.corner1 as number[]));
            const corner2 = rotatePoint(arrayToVector(data.corner2 as number[]));
            return {
                corner1: vectorToArray(corner1),
                corner2: vectorToArray(corner2),
            };
        }

        case 'box':
        case 'sphere':
        case 'cylinder': {
            const position = rotatePoint(arrayToVector(data.position as number[] || [0, 0, 0]));
            const currentRotation = (data.rotation as number[]) || [0, 0, 0];
            return {
                position: vectorToArray(position),
                rotation: [currentRotation[0], currentRotation[1] + angle, currentRotation[2]],
            };
        }

        case 'curve': {
            const points = (data.points as number[][]).map((p) => {
                return vectorToArray(rotatePoint(arrayToVector(p)));
            });
            return { points };
        }

        default:
            return {};
    }
}

/**
 * Calculate scale transformation
 */
export function calculateScale(
    entity: Entity,
    center: THREE.Vector3,
    factor: number
): Partial<Entity['data']> {
    const data = entity.data;

    const scalePoint = (point: THREE.Vector3): THREE.Vector3 => {
        const local = point.clone().sub(center);
        local.multiplyScalar(factor);
        return local.add(center);
    };

    switch (entity.type) {
        case 'line':
        case 'wall': {
            const start = scalePoint(arrayToVector(data.start as number[]));
            const end = scalePoint(arrayToVector(data.end as number[]));
            const length = start.distanceTo(end);

            return {
                start: vectorToArray(start),
                end: vectorToArray(end),
                length,
                ...(data.center ? {
                    center: vectorToArray(scalePoint(arrayToVector(data.center as number[]))),
                } : {}),
            };
        }

        case 'circle': {
            const circleCenter = scalePoint(arrayToVector(data.center as number[]));
            const radius = (data.radius as number) * factor;
            return {
                center: vectorToArray(circleCenter),
                radius,
            };
        }

        case 'arc': {
            const arcCenter = scalePoint(arrayToVector(data.center as number[]));
            const radius = (data.radius as number) * factor;
            return {
                center: vectorToArray(arcCenter),
                radius,
            };
        }

        case 'rectangle': {
            const corner1 = scalePoint(arrayToVector(data.corner1 as number[]));
            const corner2 = scalePoint(arrayToVector(data.corner2 as number[]));
            return {
                corner1: vectorToArray(corner1),
                corner2: vectorToArray(corner2),
            };
        }

        case 'box': {
            const position = scalePoint(arrayToVector(data.position as number[] || [0, 0, 0]));
            const size = (data.size as number[]).map(s => s * factor);
            return {
                position: vectorToArray(position),
                size,
            };
        }

        case 'sphere': {
            const position = scalePoint(arrayToVector(data.position as number[] || [0, 0, 0]));
            const radius = (data.radius as number) * factor;
            return {
                position: vectorToArray(position),
                radius,
            };
        }

        case 'cylinder': {
            const position = scalePoint(arrayToVector(data.position as number[] || [0, 0, 0]));
            const radiusTop = (data.radiusTop as number) * factor;
            const radiusBottom = (data.radiusBottom as number) * factor;
            const height = (data.height as number) * factor;
            return {
                position: vectorToArray(position),
                radiusTop,
                radiusBottom,
                height,
            };
        }

        case 'curve': {
            const points = (data.points as number[][]).map((p) => {
                return vectorToArray(scalePoint(arrayToVector(p)));
            });
            return { points };
        }

        default:
            return {};
    }
}

/**
 * Calculate mirror transformation
 */
export function calculateMirror(
    entity: Entity,
    mirrorPoint: THREE.Vector3,
    axis: 'x' | 'y' | 'z'
): Partial<Entity['data']> {
    const data = entity.data;

    const mirrorPt = (point: THREE.Vector3): THREE.Vector3 => {
        const result = point.clone();
        switch (axis) {
            case 'x':
                result.x = 2 * mirrorPoint.x - point.x;
                break;
            case 'y':
                result.y = 2 * mirrorPoint.y - point.y;
                break;
            case 'z':
                result.z = 2 * mirrorPoint.z - point.z;
                break;
        }
        return result;
    };

    switch (entity.type) {
        case 'line':
        case 'wall': {
            const start = mirrorPt(arrayToVector(data.start as number[]));
            const end = mirrorPt(arrayToVector(data.end as number[]));
            const wallAngle = Math.atan2(end.z - start.z, end.x - start.x);

            return {
                start: vectorToArray(start),
                end: vectorToArray(end),
                ...(data.angle !== undefined && { angle: wallAngle }),
                ...(data.center ? {
                    center: vectorToArray(mirrorPt(arrayToVector(data.center as number[]))),
                } : {}),
            };
        }

        case 'circle': {
            const circleCenter = mirrorPt(arrayToVector(data.center as number[]));
            return { center: vectorToArray(circleCenter) };
        }

        case 'arc': {
            const arcCenter = mirrorPt(arrayToVector(data.center as number[]));
            // Mirror also reverses the arc direction
            const startAngle = Math.PI - (data.endAngle as number);
            const endAngle = Math.PI - (data.startAngle as number);
            return {
                center: vectorToArray(arcCenter),
                startAngle,
                endAngle,
            };
        }

        case 'rectangle': {
            const corner1 = mirrorPt(arrayToVector(data.corner1 as number[]));
            const corner2 = mirrorPt(arrayToVector(data.corner2 as number[]));
            return {
                corner1: vectorToArray(corner1),
                corner2: vectorToArray(corner2),
            };
        }

        case 'box':
        case 'sphere':
        case 'cylinder': {
            const position = mirrorPt(arrayToVector(data.position as number[] || [0, 0, 0]));
            return { position: vectorToArray(position) };
        }

        case 'curve': {
            const points = (data.points as number[][]).map((p) => {
                return vectorToArray(mirrorPt(arrayToVector(p)));
            });
            return { points };
        }

        default:
            return {};
    }
}

/**
 * Calculate angle between two points relative to center
 */
export function calculateAngle(
    center: THREE.Vector3,
    point1: THREE.Vector3,
    point2: THREE.Vector3
): number {
    const angle1 = Math.atan2(point1.z - center.z, point1.x - center.x);
    const angle2 = Math.atan2(point2.z - center.z, point2.x - center.x);
    return angle2 - angle1;
}

/**
 * Calculate scale factor between two points relative to center
 */
export function calculateScaleFactor(
    center: THREE.Vector3,
    basePoint: THREE.Vector3,
    currentPoint: THREE.Vector3
): number {
    const baseDist = center.distanceTo(basePoint);
    const currentDist = center.distanceTo(currentPoint);
    return baseDist > 0.001 ? currentDist / baseDist : 1;
}

// ============================================================================
// ARRAY OPERATIONS
// ============================================================================

export interface ArrayConfig {
    type: 'rectangular' | 'polar' | 'path';
    // Rectangular
    columns: number;
    rows: number;
    levels: number;
    columnOffset: number;
    rowOffset: number;
    levelOffset: number;
    // Polar
    center: THREE.Vector3;
    count: number;
    totalAngle: number; // In radians (2π = full circle)
    rotateItems: boolean;
}

/**
 * Generate array positions
 */
export function generateArrayPositions(
    basePoint: THREE.Vector3,
    config: ArrayConfig
): THREE.Vector3[] {
    const positions: THREE.Vector3[] = [];

    if (config.type === 'rectangular') {
        for (let l = 0; l < config.levels; l++) {
            for (let r = 0; r < config.rows; r++) {
                for (let c = 0; c < config.columns; c++) {
                    if (c === 0 && r === 0 && l === 0) continue; // Skip original
                    positions.push(new THREE.Vector3(
                        basePoint.x + c * config.columnOffset,
                        basePoint.y + l * config.levelOffset,
                        basePoint.z + r * config.rowOffset
                    ));
                }
            }
        }
    } else if (config.type === 'polar') {
        const angleStep = config.totalAngle / config.count;
        for (let i = 1; i < config.count; i++) {
            const angle = angleStep * i;
            const radius = basePoint.distanceTo(config.center);
            const baseAngle = Math.atan2(
                basePoint.z - config.center.z,
                basePoint.x - config.center.x
            );
            positions.push(new THREE.Vector3(
                config.center.x + radius * Math.cos(baseAngle + angle),
                basePoint.y,
                config.center.z + radius * Math.sin(baseAngle + angle)
            ));
        }
    }

    return positions;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function arrayToVector(arr: number[]): THREE.Vector3 {
    return new THREE.Vector3(arr[0] || 0, arr[1] || 0, arr[2] || 0);
}

function vectorToArray(vec: THREE.Vector3): number[] {
    return [vec.x, vec.y, vec.z];
}

/**
 * Clone an entity with a new ID
 */
export function cloneEntity(entity: Entity, newId: string): Entity {
    return {
        ...entity,
        id: newId,
        name: `${entity.name}-copy`,
        data: structuredClone(entity.data),
    };
}
